import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import type { SkinConcern } from './types';
import type { UserSession, UserSkinProfile } from '../../types';
import ApiService from '../../services/api';
import { enrichSessionsWithAnnotations, mapSessionsToProfiles } from './utils';
import { MoreUserDetailsView } from './index';

export default function DistributorCustomerDataTable() {
  const {
    sessionId,
    profileId,
    token,
    distributorId,
    role,
    customerSessionsCache,
    setAnalysisSessions,
    setOutputScore,
    setProfileImageUrl,
    getTranslation,
    userProfilesCache,
    isRTL,
    setCurrentView,
    // outputScore/profileImageUrl are global and intentionally not used here (avoid flicker)
  } = useAppContext();

  const [profileData, setProfileData] = useState<UserSkinProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [, setError] = useState<Error | null>(null);

  const isDistributorRole = role === 1;

  useEffect(() => {
    if (!isDistributorRole || !token) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const fetchCustomerListing = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const api = new ApiService(sessionId, token);
        const flowType = api.getFlowTypeFromUrl();

        let sessionsToRender: UserSession[] = [];

        if (flowType === 'distributor' && distributorId) {
          const cachedSessions =
            customerSessionsCache?.data && customerSessionsCache.distributorId === distributorId
              ? customerSessionsCache.data
              : [];

          if (cachedSessions.length > 0) sessionsToRender = cachedSessions;

          const targetSession = sessionsToRender.find((s) => s._id === profileId);
          const candidateUserId = targetSession?.user_id;
          const isDistributor = targetSession?.role === 1 && targetSession?.is_anonymous === true;

          const hasUserInfo =
            !!targetSession &&
            (targetSession.first_name ||
              targetSession.last_name ||
              targetSession.email ||
              targetSession.cs_phone_number ||
              targetSession.guest_email ||
              targetSession.guest_name);

          const cachedUserInfo = hasUserInfo
            ? {
                first_name: targetSession.first_name,
                last_name: targetSession.last_name,
                email: targetSession.email,
                cs_phone_number: targetSession.cs_phone_number,
                guest_email: targetSession.guest_email,
                guest_name: targetSession.guest_name,
              }
            : null;

          const clickedSession = targetSession;

          if (candidateUserId && distributorId && !isDistributor && candidateUserId !== distributorId) {
            try {
              const response = await api.getSingleDistributorCustomer(candidateUserId, distributorId);
              if (abortController.signal.aborted) return;

              const fetchedSessions = response.data?.sessions ?? [];
              const responseAnnotations = (response.data as any)?.annotations || null;

              const enriched = enrichSessionsWithAnnotations(fetchedSessions, responseAnnotations);

              if (enriched.length > 0) {
                sessionsToRender = enriched.map((session) => {
                  if (session.user_id === candidateUserId && cachedUserInfo) {
                    return {
                      ...session,
                      first_name: session.first_name || cachedUserInfo.first_name,
                      last_name: session.last_name || cachedUserInfo.last_name,
                      email: session.email || cachedUserInfo.email,
                      cs_phone_number: session.cs_phone_number || cachedUserInfo.cs_phone_number,
                      guest_email: session.guest_email || cachedUserInfo.guest_email,
                      guest_name: session.guest_name || cachedUserInfo.guest_name,
                    };
                  }
                  return session;
                });

                if (clickedSession && !sessionsToRender.find((s) => s._id === clickedSession._id)) {
                  sessionsToRender = [clickedSession, ...sessionsToRender];
                }
              }
            } catch (singleCustomerError: any) {
              console.error('Error fetching single distributor customer:', singleCustomerError);
            }
          }

          if (sessionsToRender.length === 0 && cachedSessions.length > 0) {
            sessionsToRender = cachedSessions;
          }
        }

        if (abortController.signal.aborted) return;

        setAnalysisSessions(sessionsToRender);

        if (sessionsToRender.length > 0) {
          const mapped = mapSessionsToProfiles(sessionsToRender);
          setProfileData(mapped);

          // Customer tab should always show the latest scan for that customer.
          // mapSessionsToProfiles sorts by createdAt, so latest is the last item.
          const activeProfile = mapped[mapped.length - 1];

          if (activeProfile?.output_score?.length) {
            setOutputScore(activeProfile.output_score);
            if (activeProfile.input_image_url) setProfileImageUrl(activeProfile.input_image_url);
          }
        }
      } catch (e) {
        if (!abortController.signal.aborted) {
          setError(e as Error);
          console.error('Error fetching distributor customer sessions:', e);
        }
      } finally {
        if (!abortController.signal.aborted) setIsLoading(false);
      }
    };

    fetchCustomerListing();
    return () => abortController.abort();
  }, [
    profileId,
    token,
    distributorId,
    sessionId,
    customerSessionsCache,
    setAnalysisSessions,
    setOutputScore,
    setProfileImageUrl,
    isDistributorRole,
  ]);

  // Show latest scan for the customer
  const selectedProfile = profileData[profileData.length - 1];

  // Never fall back to global outputScore; it can belong to a previous page/tab.
  const scoreSource = selectedProfile?.output_score?.length ? selectedProfile.output_score : null;

  const { skinOutput, score } = useMemo(() => {
    const empty = { skinOutput: [] as SkinConcern[], score: 0 };
    if (!scoreSource || scoreSource.length === 0) return empty;
    const skin_health = scoreSource.find((i) => i.concern === 'skin_health');
    const sorted = scoreSource.filter((i) => i.concern !== 'skin_health').sort((a, b) => a.score - b.score);
    return { skinOutput: sorted, score: skin_health ? skin_health.score : 0 };
  }, [scoreSource]);

  // Avoid outputScore fallback profile during distributor->customer to reduce flicker
  useEffect(() => {
    // no-op: keeping loader until profileData populated
  }, []);

  const [selectedAnnotationImage, setSelectedAnnotationImage] = useState<string | null>(null);
  const [selectedConcernId, setSelectedConcernId] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [fullImageModalOpen, setFullImageModalOpen] = useState(false);

  const handleCopyEmail = async () => {
    if (!selectedProfile?.email) return;
    await navigator.clipboard.writeText(selectedProfile.email);
  };

  const handleConcernClick = (issue: SkinConcern) => {
    const url = selectedProfile?.annotations?.[issue.concern];
    if (!url) return;
    setSelectedConcernId(issue.concern);
    setImageLoading(true);
    setIsImageLoaded(false);
    const img = new Image();
    img.onload = () => {
      setSelectedAnnotationImage(url);
      setIsImageLoaded(true);
      setImageLoading(false);
    };
    img.onerror = () => {
      setSelectedAnnotationImage(url);
      setIsImageLoaded(true);
      setImageLoading(false);
    };
    img.src = url;
  };

  const handleCloseAnnotationModal = () => {
    setSelectedAnnotationImage(null);
    setSelectedConcernId(null);
    setImageLoading(true);
    setIsImageLoaded(false);
  };

  const handleNavigateBack = () => setCurrentView('distributor-welcome');

  return (
    <MoreUserDetailsView
      role={role}
      profileId={profileId}
      isRTL={isRTL}
      getTranslation={getTranslation}
      userProfilesCache={userProfilesCache}
      skinOutput={skinOutput}
      score={score}
      selectedAnnotationImage={selectedAnnotationImage}
      selectedConcernId={selectedConcernId}
      imageLoading={imageLoading}
      isImageLoaded={isImageLoaded}
      fullImageModalOpen={fullImageModalOpen}
      moreProfileData={profileData}
      userProfileData={{}}
      filteredProfileData={profileData}
      selectedProfile={selectedProfile}
      isLoadingDetails={isLoading}
      setFullImageModalOpen={setFullImageModalOpen}
      handleCopyEmail={handleCopyEmail}
      handleConcernClick={handleConcernClick}
      handleCloseAnnotationModal={handleCloseAnnotationModal}
      handleNavigateBack={handleNavigateBack}
    />
  );
}

